import React, { useMemo, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Button, Transfer, Modal, SelectOptionWithGroup, SearchBox, AutoComplete } from 'tea-component';
import Loading from '@/pages/intelligence/components/Loading';
import RenderLeftCell from './components/RenderLeftCell';
import ShortCutCell from './components/ShortCutCell';
import TargetTable from './components/TargetTable';
import { getEntitiesKeys, normalizeMobileSearchGames } from './utils';
import { searchAllGames } from '@/pages/intelligence/services/filter';
import { useIntl, getLocale } from 'umi';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import type { AllGameSelectorProps, SelectGames } from './types';
import './index.scss';
import { getTextWithHighlight } from '@/pages/intelligence/utils/commonUtils';
import { useDebounceFn, useDeepCompareEffect } from 'ahooks';
import DefaultEntityRenderer from '@/pages/intelligence/pages/Compare/PcConsoleGames/components/CompareCore/renderers/EntityRenderer';
import { GameSegmentType } from '@/pages/intelligence/pages/PCConsoleBenchmark/types';

/**
 * 通用游戏筛选器-弹窗组件
 */
export default function AllGameSelector(props: AllGameSelectorProps) {
  // 中英翻译
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const {
    // 获取勾选游戏实体对应id的key
    entityKey = 'id',
    // 弹窗标题及默认值
    title = formatMessage({ id: 'dbi_select_games' }),
    // 弹窗右侧列表标题及默认值
    rightCellTitle = formatMessage({ id: 'dbi_games' }),
    // 是否展示弹窗
    visible,
    // 搜索框placeholder
    searchPlaceholder = formatMessage({ id: 'dbi_search_any_game' }),
    // 搜索没结果的提示文案
    noSearchResultTips = formatMessage({ id: 'dbi_no_search_results' }),
    // 勾选提示文案
    pleaseSelectTips = formatMessage({ id: 'dbi_please_a_select_game' }),
    // 空态文案
    notSelectTips = '',
    // 筛选器点击提交事件
    onConfirm,
    // 游戏筛选器关闭事件回调
    onClose,
    // 获取关键字的方法，用户输入部分关键字，后台返回有效的关键字
    keywordsFetcher,
    // 弹窗标题提示内容
    tipText,
    // 组件已勾选游戏列表
    selectedGameList,
    // 设置筛选器按钮文案显示内容方法
    getButtonLabel,
    // 获取All Games游戏筛选器外部传入接口
    searchAllGamesApi,
    // 从外部传入组件参数
    params,
    // 接收外部传入游戏实体展示内容
    entityRenderer: entityRendererOutter,
    chooseUpNum = 1000,
    fixedSelectionGameKeyList = [],
    // 固选的游戏实体数据
    fixedSelectionGameEntities = [],
    emptyGameAllText = 'dbi_all',
    setIsGameEmpty,
    hasShortCut = false,
    limitMsg = formatMessage({ id: 'dbi_compare_select_games_limit_tips' }, { max: chooseUpNum || 6 }),
    // 快捷选项列表
    shortcutList,
    // 快捷点击函数
    onShortcutClick,
    // 选中的快捷选项
    selectedShortcutActive,
    // 点击快捷后自动勾选游戏
    shortcutAddGame,
    // 是否展示快捷标题
    isShowShortcutTitle,
    // 弹窗className
    transferModalClassName,
    // 设置选中快捷
    setSelectedShortcutActive,
    // 快捷选项展示英文名称
    shortCutNameEn,
  } = props;

  // console.log('AllGameSelector', props);

  // 从外部传入参数，获取游戏类型与游戏类别
  // 游戏类型 ：current_game（除了 New Release和Upcoming ）, new_release(New Release页面), upcoming (Upcoming 页面)
  // 带下划线参数名，不能通过eslint检查，加别名
  const {
    entity_type: entityType = 'pc',
    game_type: gameType = 'current_game',
    // 是否需要展示上线时间
    hasReleaseTime,
    ...extraSearchParams
  } = params || ({} as any);
  // 是否加载中
  const [isLoading, setIsLoading] = useState(false);
  // 是否加载中-下滑滚动加载
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  // 是否展示超出勾选限制提示
  const [isOverOneThousTips, setIsOverOneThousTips] = useState(false);
  // 游戏列表翻页页码
  const [normalPage, setNormalPage] = useState(1);
  // 游戏列表当前所有数据
  const [gamesAllList, setGamesAllList] = useState<SelectGames[]>([]);
  // 是否还有更多数据
  const [ifHasMoreGames, setIfHasMoreGames] = useState(false);
  // 实体搜索关键字
  const [keyword, setKeyword] = useState('');
  // 勾选的游戏实体数据
  const [checkedEntities, setCheckedEntities] = useState<SelectGames[]>([]);
  // 是否显示搜索联想弹窗
  const [isOpen, setIsOpen] = useState(false);
  // 搜索联想词数据列
  const [keywordList, setKeywordList] = useState<SelectOptionWithGroup[]>([]);
  // 搜索游戏快捷勾选数据
  const [gameActive, setGameActive] = useState<{
    [key in GameSegmentType]?: boolean;
  }>({
    'POE-like': false,
    'Tarkov-like': false,
    'Dying Light 2-Related': false,
  });

  // 快捷搜索获取游戏列表
  useDeepCompareEffect(() => {
    if (selectedShortcutActive) {
      const newGameActive: { [key: string]: boolean } = {};
      selectedShortcutActive.forEach((key) => {
        newGameActive[key] = true;
      });
      setGameActive(newGameActive);
      searchGamesList(keyword, 1, false);
      setIsLoading(true);
    }
  }, [selectedShortcutActive]);

  // 游戏列表当前数据--快捷栏
  const [gamesShortList, setGamesShortList] = useState<SelectGames[]>([]);

  // 用于保存每次请求时返回的 es_ids，后续查询参数需携带
  const esIDsRef = useRef([]);

  const resolveEntityKey = useMemo(() => {
    return (record: any) => {
      if (!record) return undefined;
      const candidates = [entityKey, 'app_id', 'appId', 'id', 'unified_id', 'game_id', 'gameId'];
      for (const key of candidates) {
        const value = key && record?.[key];
        if (value !== undefined && value !== null && `${value}` !== '') {
          return value;
        }
      }
      return undefined;
    };
  }, [entityKey]);

  const normalizeSearchGames = (games: SelectGames[]) => {
    if (entityType !== 'mobile') {
      return games;
    }

    return normalizeMobileSearchGames(games) as SelectGames[];
  };

  const normalizeSelectedGamesOnOpen = (games: SelectGames[]) => {
    return normalizeSearchGames(games);
  };

  // 若已勾选游戏，重新打开时，回填已选游戏
  useDeepCompareEffect(() => {
    let isUnmounted = false;
    const updateSelectedGames = async () => {
      if (!Array.isArray(selectedGameList)) return;
      const normalizedSelectedGameList = normalizeSelectedGamesOnOpen(selectedGameList);
      setCheckedEntities(normalizedSelectedGameList);

      const missingCoverGames = normalizedSelectedGameList?.filter((item: any) => !item?.cover) || [];
      if (!missingCoverGames.length) return;

      const missingIds = Array.from(
        new Set(
          missingCoverGames
            .map((item: any) => resolveEntityKey(item))
            .filter((id: string | number | undefined) => id !== undefined && id !== null && `${id}` !== ''),
        ),
      );

      if (!missingIds.length) return;

      const searchAllGamesFun = searchAllGamesApi instanceof Function ? searchAllGamesApi : searchAllGames;
      try {
        const requestParams: any = {
          page: 1,
          page_size: Math.max(missingIds.length, 1),
          search_by_name: '',
          entity_type: entityType,
          game_type: gameType,
          filter_ids: missingIds,
          ...extraSearchParams,
        };
        const res = await searchAllGamesFun(requestParams);
        const gameList = normalizeSearchGames(res?.data?.game_list || []);
        if (!Array.isArray(gameList) || !gameList.length) return;

        const gameMap = new Map<string | number, SelectGames>();
        gameList.forEach((item: any) => {
          const key = resolveEntityKey(item);
          if (key !== undefined && key !== null) {
            gameMap.set(key, item as SelectGames);
          }
        });

        if (!gameMap.size) return;

        const mergedList = normalizedSelectedGameList.map((item: any) => {
          const key = resolveEntityKey(item);
          if (key === undefined || key === null) return item;
          const fetched = gameMap.get(key);
          if (!fetched) return item;
          const coverFromFetched =
            fetched?.cover ||
            fetched?.cover_url ||
            fetched?.console_cover ||
            fetched?.pc_cover ||
            fetched?.pcCover ||
            fetched?.consoleCover ||
            fetched?.thumbnail ||
            fetched?.img ||
            fetched?.logo;
          return {
            ...fetched,
            ...item,
            cover: coverFromFetched || item?.cover || '',
            entityType: fetched?.entityType || fetched?.entity_type || item?.entityType,
          } as SelectGames;
        });

        if (!isUnmounted) {
          setCheckedEntities(mergedList);
        }
      } catch (error) {
        console.error(error);
      }
    };

    updateSelectedGames();

    return () => {
      isUnmounted = true;
    };
  }, [selectedGameList, entityKey, entityType, gameType, extraSearchParams, searchAllGamesApi, resolveEntityKey]);

  //  获取勾选的实体数据的keys--游戏id数组
  const checkedEntitiesKeys = useMemo<string[]>(
    () => getEntitiesKeys(checkedEntities, entityKey),
    [entityKey, checkedEntities],
  );

  // 根据游戏勾选数据，展示游戏组件按钮对应文案
  useEffect(() => {
    const gamesCount = checkedEntities?.length || 0;
    setIsGameEmpty?.(gamesCount < 1);
    let result = '';
    if (!gamesCount) {
      // 没选择游戏 或者 全选游戏(包含 非搜索状态 和 搜索状态)
      result = formatMessage({ id: emptyGameAllText });
    } else if (gamesCount === 1) {
      // 选择游戏数量小于2个
      result = checkedEntities[0].name;
    } else {
      // 选择游戏数量 大于 3 且 小于 全部游戏(区分 非搜索状态 和 搜索状态)
      result = String(gamesCount);
    }
    getButtonLabel?.(result);
  }, [checkedEntities, locale, getButtonLabel, formatMessage, setIsGameEmpty, emptyGameAllText]);

  // 实体搜索关键字change
  const handleKeywordChange = (payload: string) => {
    setKeyword(payload);
    setNormalPage(1);
    // 关键词更改重置 esIDsRef
    esIDsRef.current = [];
    // 若搜索词为空，则不调数据
    if (payload?.trim() === '' && !selectedShortcutActive) {
      setGamesAllList([]);
    } else {
      searchGamesList(payload, 1, false);
    }
  };

  // 移除游戏
  const handleRemoveCheckedEntity = (payload: string) => {
    const checkedEntitiesTemp: SelectGames[] =
      checkedEntities?.filter((item: any) => item?.[entityKey] !== payload) || [];
    setCheckedEntities(checkedEntitiesTemp);
  };

  // 清除所有勾选游戏
  const handleClearAll = () => {
    setCheckedEntities([]);
    setIsOverOneThousTips(false);
    if (!selectedShortcutActive) {
      setGameActive({
        'POE-like': false,
        'Tarkov-like': false,
        'Dying Light 2-Related': false,
      });
      setGamesShortList([]);
    }
  };

  // 提交勾选游戏事件
  const handleConfirm = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    e?.preventDefault();
    const normalizedFixedSelectionGameEntities = normalizeSearchGames(fixedSelectionGameEntities);
    // 固选游戏需要回传到列表
    onConfirm?.(
      [...checkedEntitiesKeys, ...fixedSelectionGameKeyList],
      [...checkedEntities, ...normalizedFixedSelectionGameEntities],
    );
  };

  // 取消勾选游戏事件，并且回显之前已选择游戏数据
  const handleCancel = (e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.stopPropagation();
    e?.preventDefault();
    setCheckedEntities(selectedGameList || []);
    onClose?.();
  };

  // 获取游戏列表数据
  const searchGamesList = async (keyword: string, pageNum: number, isLoadMore = false) => {
    if (isMoreLoading) return;
    if (isLoading) return;
    if (!isLoadMore) {
      setIsLoading(true);
    } else {
      setIsOverOneThousTips(false);
      setIsMoreLoading(true);
    }
    try {
      const requestParams = {
        page: pageNum,
        page_size: 100, // 搜索游戏，往下滑动，第页搜索100条数据
        search_by_name: keyword,
        entity_type: entityType,
        game_type: gameType,
        es_ids: esIDsRef.current,
        ...extraSearchParams,
      } as any;

      // 如过有快捷搜索选项，入参添加 genre_type
      if (Array.isArray(shortcutList) && shortcutList.length > 0) {
        requestParams.genre_type = selectedShortcutActive;
      }
      // 若从外部传入获取搜索游戏接口，则优先使用
      const searchAllGamesFun = searchAllGamesApi instanceof Function ? searchAllGamesApi : searchAllGames;
      // 接口查询之前先重置搜索数据，以防报204，无法更新数据

      const res = await searchAllGamesFun(requestParams);
      if (!Array.isArray(res?.data?.game_list)) {
        setGamesAllList([]);
        setIfHasMoreGames(false);
        return;
      }
      const { data = {} } = res;
      const {
        game_list: gameListTemp = [],
        is_next_page: isNextPage = false,
        es_ids: esIDs = [],
      } = data || ({} as any);
      let gameList = Array.isArray(gameListTemp) ? gameListTemp : [];
      esIDsRef.current = esIDs;
      gameList = normalizeSearchGames(gameList);

      // 过滤掉要固选的已选游戏
      if (Array.isArray(fixedSelectionGameKeyList) && fixedSelectionGameKeyList?.length > 0) {
        gameList = gameList?.filter((item: SelectGames) => !fixedSelectionGameKeyList?.includes(item.id));
      }

      let newGameList = [...gameList];
      // 如果是下翻,总游戏加上之前已搜索结果
      if (isLoadMore) {
        newGameList = [...gamesAllList, ...gameList];
      }
      // 每次调取接口，重新赋值下拉所有游戏,并且重新组装
      setGamesAllList(newGameList);
      // 根据后端返回是否还有下一页数据，来更新下页数据状态
      setIfHasMoreGames(isNextPage);
    } catch (e) {
      console.error(e);
      setIfHasMoreGames(false);
      setGamesAllList([]);
    } finally {
      if (!isLoadMore) {
        setIsLoading(false);
      } else {
        setIsMoreLoading(false);
      }
    }
  };

  // 重置部分组件数据
  const resetData = () => {
    setKeyword('');
    setIsMoreLoading(false);
    setIsOverOneThousTips(false);
    setNormalPage(1);
    if (shortcutList) {
      setSelectedShortcutActive([shortcutList?.[0]?.value]);
    }
    // 如果没传入快捷列表,则执行原有代码逻辑
    if (!shortcutList) {
      setGameActive({
        'POE-like': false,
        'Tarkov-like': false,
        'Dying Light 2-Related': false,
      });
      setGamesAllList([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 弹窗关闭时，重置部分组件数据
    if (!visible) {
      resetData();
    }
  }, [visible]);

  // 向下滑触底事件，若有下一页数据，则增加页码并获取下一页数据
  const handleLoadMore = () => {
    // 快捷选项不需要判断keyword为空
    const shouldStop =
      (!shortcutList && (!ifHasMoreGames || isLoading || keyword?.trim() === '')) ||
      (shortcutList && (!ifHasMoreGames || isLoading));
    if (shouldStop) return;
    const newPage = normalPage + 1;
    setNormalPage(newPage);
    searchGamesList(keyword, newPage, true);
  };

  // 关闭打开联想词下拉展示
  const handleOpenChange = (payload: boolean) => {
    setIsOpen(payload);
  };

  // 获取下拉联想词方法
  const updateKeywords = async (payload: string) => {
    try {
      const params: any = {
        entity_type: entityType,
        keyword: payload,
        system_type: entityType,
        complement: '1',
      };
      // 如果快捷搜索添加 genre_type 参数 ##后端支持后放开
      // if (Array.isArray(shortcutList) && shortcutList.length > 0) {
      //   params.genre_type = selectedShortcutActive;
      // }
      const data = await keywordsFetcher?.(params);
      const list = Array.isArray(data?.result) ? data?.result : [];
      const keywordsTemp = list?.map((item: any) => ({
        value: item,
        text: getTextWithHighlight(item, payload),
      }));
      setKeywordList(keywordsTemp);
    } catch (exception) {}
  };

  /**
   * useDebounceFn 是一个用于延迟函数执行的 Hook。
   * useDebounceFn接收一个函数和延迟时间作为参数，返回一个对象，该对象包含一个run方法
   */
  const { run: updateKeywordsRun } = useDebounceFn(updateKeywords, {
    wait: 300,
  });

  // 搜索区域搜索值改变事件
  const handleSearchBoxKeywordChange = (payload: string) => {
    if (!payload) {
      setKeywordList([]);
      setIsOpen(false);
      handleKeywordChange?.('');
      return;
    }
    setKeyword(payload);
    updateKeywordsRun?.(payload);
  };

  // 选择联想词事件回调
  const handleKeywordSelect = (payload?: string) => {
    const keywordTemp = payload || '';
    setKeyword(keywordTemp);
    setIsOpen(false);
    handleKeywordChange?.(keywordTemp);
  };

  const notSelectTipsCur = useMemo(() => {
    if (notSelectTips !== '') {
      return formatMessage({ id: notSelectTips });
    }
    return checkedEntities?.length > 0 ? (
      formatMessage({ id: 'dbi_compare_please_search_game_only' })
    ) : (
      <>
        {formatMessage({ id: 'dbi_compare_not_select_game_yet' })}
        <br />
        {formatMessage({ id: 'dbi_compare_please_search_game' })}
      </>
    );
  }, [notSelectTips, checkedEntities, formatMessage]);

  const isShortCutPage = useMemo(() => {
    if (keyword?.trim() === '' && hasShortCut) {
      return true;
    }
    return false;
  }, [hasShortCut, keyword]);

  const isDisabled = useMemo<boolean>(
    () => checkedEntitiesKeys?.length >= chooseUpNum,
    [checkedEntitiesKeys?.length, chooseUpNum],
  );

  const showEntity = !(hasShortCut && !shortcutList);

  // 筛选器组件左侧内容区域
  const renderLeftCell = () => {
    if (isLoading) return <Loading />;
    if (keyword?.trim() === '' && !hasShortCut) {
      return <div className="dbi-table-no-data dbi-left-cell">{notSelectTipsCur}</div>;
    }
    if (isShortCutPage) {
      return (
        <ShortCutCell
          keyword={keyword}
          gamesAllList={gamesAllList}
          entityType={entityType}
          checkedEntitiesKeys={checkedEntitiesKeys}
          entityKey={entityKey}
          setCheckedEntities={setCheckedEntities}
          isMoreLoading={isMoreLoading}
          setIsOverOneThousTips={setIsOverOneThousTips}
          handleLoadMore={handleLoadMore}
          checkedEntities={checkedEntities}
          entityRenderer={entityRendererWithKeyword}
          showEntity={showEntity}
          chooseUpNum={chooseUpNum}
          noSearchResultTips={notSelectTips}
          gameActive={gameActive}
          setGameActive={setGameActive}
          hasShortCut={hasShortCut}
          isShortCutPage={isShortCutPage}
          gamesShortList={gamesShortList}
          setGamesShortList={setGamesShortList}
          isDisabled={isDisabled}
          disabledMsg={limitMsg}
          shortcutList={shortcutList}
          onShortcutClick={onShortcutClick}
          shortcutAddGame={shortcutAddGame}
          isShowShortcutTitle={isShowShortcutTitle}
          setNormalPage={setNormalPage}
          shortCutNameEn={shortCutNameEn}
        />
      );
    }
    return (
      <RenderLeftCell
        keyword={keyword}
        gamesAllList={gamesAllList}
        noSearchResultTips={noSearchResultTips}
        entityType={entityType}
        checkedEntitiesKeys={checkedEntitiesKeys}
        entityKey={entityKey}
        setCheckedEntities={setCheckedEntities}
        isMoreLoading={isMoreLoading}
        setIsOverOneThousTips={setIsOverOneThousTips}
        handleLoadMore={handleLoadMore}
        checkedEntities={checkedEntities}
        entityRenderer={entityRendererWithKeyword}
        showEntity={showEntity}
        chooseUpNum={chooseUpNum}
        hasShortCut={hasShortCut}
        isDisabled={isDisabled}
        disabledMsg={limitMsg}
      />
    );
  };

  const entityRenderer = typeof entityRendererOutter === 'function' ? entityRendererOutter : DefaultEntityRenderer;

  const entityRendererWithKeyword = (tablePayload: any) =>
    entityRenderer(tablePayload, '', hasShortCut, showEntity, hasReleaseTime);

  return (
    <Modal
      className="dbi-add-entities-modal dbi-games-select-modal-common"
      visible={visible}
      caption={
        <div className="dbi-add-title">
          {title}
          {tipText && <TipContext className="dbi-add-title-icon" tipText={tipText} />}
        </div>
      }
      size={800}
      onClose={handleCancel}
    >
      <Modal.Body>
        <Transfer
          className={classnames(
            'dbi-games-select-modal-common-transfer selection-centered dbi-all-games-selector',
            'benchmark-customize-transfer-r',
            'benchmark-customize-transfer-l ',
            'dbi-allgame-selector-auto-compare-pcconsole-empty',
            transferModalClassName,
            {
              empty: !selectedGameList?.length && notSelectTips === 'dbi_compare_pcconsole_game_selection_empty',
            },
          )}
          leftCell={
            <Transfer.Cell
              scrollable={false}
              title={formatMessage({ id: 'dbi_game_add' })}
              header={
                <div className="dbi-mobile-compare-keyword-select">
                  <AutoComplete
                    open={!!keyword && isOpen}
                    placementOffset={0}
                    overlayClassName={classnames(
                      'dbi-mobile-compare-keyword-select-overlay dbi-allgame-selector-auto-overlay',
                      {
                        empty: !keywordList?.length,
                      },
                    )}
                    options={keywordList}
                    onOpenChange={handleOpenChange}
                    onChange={handleKeywordSelect}
                  >
                    {(ref) => (
                      <SearchBox
                        ref={ref}
                        placeholder={searchPlaceholder}
                        value={keyword}
                        onSearch={handleKeywordSelect}
                        onChange={handleSearchBoxKeywordChange}
                      />
                    )}
                  </AutoComplete>
                </div>
              }
            >
              {renderLeftCell()}
            </Transfer.Cell>
          }
          rightCell={
            <Transfer.Cell
              className="dbi-compare-right"
              scrollable={false}
              title={
                <div className="compare-title-flex">
                  <div className="compare-title">
                    {formatMessage({ id: 'dbi_compare_selected' })} ({checkedEntitiesKeys?.length})
                  </div>
                  {checkedEntities?.length > 0 && (
                    <Button type="link" className="compare-btn" onClick={handleClearAll}>
                      {formatMessage({ id: 'dbi_compare_clear_all' })}
                    </Button>
                  )}
                </div>
              }
            >
              <TargetTable
                className={classnames({
                  'is-no-data': !checkedEntities?.length,
                })}
                title={rightCellTitle}
                pleaseSelectTips={pleaseSelectTips}
                entityKey={entityKey}
                records={checkedEntities}
                onRemove={handleRemoveCheckedEntity}
                entityType={entityType}
                // 扩展是否展示上线时间
                entityRenderer={(...acc) => entityRenderer(...acc, hasReleaseTime)}
                showEntity={showEntity}
                hasShortCut={hasShortCut}
              />
            </Transfer.Cell>
          }
        />
      </Modal.Body>
      {/* 游戏勾选超过1000，显示禁选提示内容 */}
      {isOverOneThousTips && (
        <div className="dbi-allgame-tips-div dbi-loading-div-allgame">
          {isOverOneThousTips &&
            formatMessage({ id: 'dbi_Choose_up_to_games' }) +
              chooseUpNum +
              formatMessage({ id: 'dbi_Choose_up_to_games_end' })}
        </div>
      )}
      <Modal.Footer>
        <Button type="primary" onClick={handleConfirm}>
          {formatMessage({ id: 'dbi_confirm' })}
        </Button>
        <Button type="weak" onClick={handleCancel}>
          {formatMessage({ id: 'dbi_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export type { AllGameSelectorProps };
